import {
  ContentEditableHtmlEditorPlugin,
  ContentEditableTextEditorPlugin,
  createContentEditableTextEditor,
  HtmlEditService,
  htmlToText,
} from '@frontapp/editor-html';
import {useEditor} from '@frontapp/editor-html/lib/react';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';

/*
 * Custom hook.
 */

export const useSearchBarEditor = (
  initialText = '',
  onPasteFn?: Handler,
  plugins: ReadonlyArray<ContentEditableTextEditorPlugin> = [],
) =>
  useEditor(() => {
    const newEditor = createContentEditableTextEditor({
      plugins: [createOnPastePlugin(onPasteFn), ...plugins],
      shouldNativeDelete: true,
      hasNewDeleteSelection: true,
    });

    newEditor.services.text.setText(initialText);

    return newEditor;
  }, []);

/*
 * Plugins.
 */

function createOnPastePlugin(onPasteFn?: Handler): ContentEditableHtmlEditorPlugin {
  return {
    handleDataTransfer: (dataTransfer: DataTransfer, editService: HtmlEditService) => {
      if (onPasteFn) {
        onPasteFn();
      }

      // We want our ContentEditable to behave like a single line input form.
      const text = findDataTransferText(dataTransfer);
      if (text) {
        editService.insertText(collapseWhitespacesAndLineBreaks(text));
      }

      // If we can't convert the pasted contents to text, do not paste anything
      // and do not fallback to editor-html's createCoreAfterPlugin.
      return true;
    },
  };
}

function findDataTransferText(dataTransfer: DataTransfer): string | undefined {
  // This should handle most cases where the DataTransfer can be
  // converted to plain text. We can add more if we want special
  // handling of other formats such as XML or images. We could even
  // have custom formats when copying from other parts of the app.

  const text = dataTransfer.getData('text/plain');
  if (text) {
    return text;
  }

  const html = dataTransfer.getData('text/html');
  if (html) {
    return htmlToText(html);
  }

  // We don't support anything else for now.
  return undefined;
}

function collapseWhitespacesAndLineBreaks(text: string): string {
  return text.trim().replace(/\s+/gi, ' ');
}
